import { Button, Checkbox, FormControlLabel, TextField } from "@mui/material";
import {
  kiosk_movementType,
  kiosk_sector,
  kiosk_terminal,
  PLATFORM,
  posAppId,
  posTillNo,
} from "config/envConfig";
import { useConfig } from "context/configContext";
import { DeliveryOptionContext } from "context/deliveryOptionContext";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import apiList from "services/apiList";
import { callApi } from "services/callApi";
import {
  ALLOW_ALL_STORES_KEY,
  DATA_REFERENCE,
  ENABLE_SCAN_KEY,
  KIOSK_ID_KEY,
  MOVEMENT_KEY,
  PRINTER_NAME_KEY,
  PRINTER_USED_KEY,
  RECEIPT_WIDTH_KEY,
  SCAN_MODE_KEY,
  SECTOR_KEY,
  STORE_LIST_KEY,
  STORE_WAYPOINTS_KEY,
  TERMINAL_KEY,
} from "utils/Constants";
import moment from "utils/momentWrapper";

export default function ConfigView() {
  const { config: appConfig, timezone } = useConfig();
  const useDelivery = useContext(DeliveryOptionContext);
  const savedConfig = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(DATA_REFERENCE)) || {};
    } catch {
      return {};
    }
  }, []);

  const [appId, setAppId] = useState(savedConfig.app_id || posAppId);
  const [posTillNumber, setPosTillNumber] = useState(
    savedConfig.posTillNo || posTillNo
  );
  const [allowAllStores, setAllowAllStores] = useState(
    savedConfig[ALLOW_ALL_STORES_KEY] ?? true
  );
  const [selectedStores, setSelectedStores] = useState(
    savedConfig[STORE_LIST_KEY] || []
  );
  const [kioskId, setKioskId] = useState(savedConfig[KIOSK_ID_KEY] || "");
  const [enableScan, setEnableScan] = useState(
    savedConfig[ENABLE_SCAN_KEY] ?? false
  );
  const [scanMode, setScanMode] = useState(
    savedConfig[SCAN_MODE_KEY] || "scanOnly"
  );
  const [printerUsed, setPrinterUsed] = useState(
    savedConfig[PRINTER_USED_KEY] ?? false
  );
  const [printerName, setPrinterName] = useState(
    savedConfig[PRINTER_NAME_KEY] || ""
  );
  const [receiptWidth, setReceiptWidth] = useState(
    String(savedConfig[RECEIPT_WIDTH_KEY] || 40)
  );

  const [storeOptions, setStoreOptions] = useState([]);
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [storeError, setStoreError] = useState("");
  const [hasTriggeredInitialStoreLoad, setHasTriggeredInitialStoreLoad] =
    useState(false);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const hasLocationBootstrap = Object.keys(appConfig || {}).length > 0;

  const fetchStores = useCallback(async () => {
    if (!hasLocationBootstrap) {
      return;
    }

    try {
      setIsLoadingStores(true);
      setStoreError("");

      const response = await callApi.get(apiList.stores.getStores, {
        isRetail: true,
        location: "",
        sector: "",
        section: "",
        supportedFulfillmentTypes:
          useDelivery.selectedDeliveryOption?.deliveryOption || "",
        orderDateTime: moment(new Date(), "UTC", "UTC").format(),
        domain: appConfig?.domain || "",
        timezone,
        platform: PLATFORM,
      });

      if (response?.status === 200) {
        const indoor = response?.data?.indoor || [];
        const outdoor = response?.data?.outdoor || [];

        const stores = [...indoor, ...outdoor].sort((a, b) =>
          (a.storeDisplayName || "").localeCompare(b.storeDisplayName || "")
        );

        setStoreOptions(stores);
      } else {
        setStoreOptions([]);
        setStoreError("Unable to load stores. Please retry.");
      }
    } catch (error) {
      console.error("ConfigView getStores failed", error);
      setStoreOptions([]);
      setStoreError("Unable to load stores. Please retry.");
    } finally {
      setIsLoadingStores(false);
    }
  }, [appConfig?.domain, hasLocationBootstrap, savedConfig, timezone]);

  useEffect(() => {
    if (hasTriggeredInitialStoreLoad) {
      return;
    }

    if (!hasLocationBootstrap) {
      return;
    }

    setHasTriggeredInitialStoreLoad(true);
    fetchStores();
  }, [
    fetchStores,
    hasLocationBootstrap,
    hasTriggeredInitialStoreLoad,
    savedConfig,
  ]);

  const toggleStore = (storeId) => {
    setSelectedStores((prev) =>
      prev.includes(storeId)
        ? prev.filter((id) => id !== storeId)
        : [...prev, storeId]
    );
  };

  const selectedStoreNames = storeOptions
    .filter((store) => selectedStores.includes(store._id))
    .map((store) => store.storeDisplayName)
    .join(", ");

  const handleSetConfig = () => {
    const waypointMap = {};
    if (!allowAllStores && selectedStores.length > 0) {
      selectedStores.forEach((id) => {
        const match = storeOptions.find((store) => store._id === id);
        if (match?.waypointId) {
          waypointMap[id] = {
            waypointId: match.waypointId,
            locationId: match.locationId,
          };
        }
      });
    }

    const config = {
      ...savedConfig,
      app_id: appId,
      posTillNo: posTillNumber,
      [ALLOW_ALL_STORES_KEY]: allowAllStores,
      [STORE_LIST_KEY]: allowAllStores ? [] : selectedStores,
      [STORE_WAYPOINTS_KEY]:
        allowAllStores || Object.keys(waypointMap).length === 0
          ? {}
          : waypointMap,
      [KIOSK_ID_KEY]: kioskId,
      [ENABLE_SCAN_KEY]: enableScan,
      [SCAN_MODE_KEY]: enableScan ? scanMode : "scanOnly",
      [PRINTER_USED_KEY]: printerUsed,
      [PRINTER_NAME_KEY]: printerUsed ? printerName.trim() : "",
      [RECEIPT_WIDTH_KEY]: printerUsed ? Number(receiptWidth) || 40 : 40,
    };

    localStorage.setItem(DATA_REFERENCE, JSON.stringify(config));
    window.location.reload();
  };

  return (
    <div
      className="absolute w-dvw h-dvh bg-gray-200 flex flex-col items-center gap-4 py-6"
      style={{
        zIndex: 99999,
      }}
    >
      <TextField
        label="POS App ID"
        variant="outlined"
        value={appId}
        onChange={(e) => setAppId(e.target.value)}
        style={{
          width: "calc(100% - 64px)",
        }}
      />
      <TextField
        label="POS Till Number"
        variant="outlined"
        value={posTillNumber}
        onChange={(e) => setPosTillNumber(e.target.value)}
        style={{
          width: "calc(100% - 64px)",
        }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={allowAllStores}
            onChange={(e) => setAllowAllStores(e.target.checked)}
          />
        }
        label="Allow all stores"
        style={{
          width: "calc(100% - 64px)",
          alignSelf: "flex-start",
          marginLeft: "32px",
        }}
      />
      <div
        style={{
          width: "calc(100% - 64px)",
        }}
      >
        <div
          style={{
            ...inputStyle,
            cursor: allowAllStores ? "not-allowed" : "pointer",
            opacity: allowAllStores ? 0.6 : 1,
          }}
          onClick={(e) => {
            if (!allowAllStores) setDropdownOpen(!dropdownOpen);
          }}
        >
          {selectedStoreNames || "Select stores"}
        </div>

        {!allowAllStores && (isLoadingStores || storeError) && (
          <div style={statusRowStyle}>
            <span>{isLoadingStores ? "Loading stores..." : storeError}</span>
            {!!storeError && (
              <Button
                size="small"
                variant="outlined"
                onClick={fetchStores}
                disabled={isLoadingStores}
              >
                Retry
              </Button>
            )}
          </div>
        )}

        {dropdownOpen && !allowAllStores && (
          <div
            style={{
              background: "#fff",
              border: "1px solid #ccc",
              maxHeight: "300px",
              overflowY: "auto",
              zIndex: 100,
              marginTop: "8px",
            }}
          >
            {isLoadingStores && <div style={optionStyle}>Loading...</div>}

            {!isLoadingStores && storeError && (
              <div style={optionStyle}>{storeError}</div>
            )}

            {!isLoadingStores && !storeError && storeOptions.length === 0 && (
              <div style={optionStyle}>No stores available</div>
            )}

            {storeOptions.map((store) => (
              <label key={store._id} style={optionStyle}>
                <input
                  type="checkbox"
                  checked={selectedStores.includes(store._id)}
                  onChange={() => toggleStore(store._id)}
                />

                {store.storeDisplayName || store.name}
              </label>
            ))}
          </div>
        )}
      </div>
      <TextField
        label="Kiosk ID"
        variant="outlined"
        value={kioskId}
        onChange={(e) => setKioskId(e.target.value)}
        disabled={enableScan && scanMode === "scanOnly"}
        style={{
          width: "calc(100% - 64px)",
          opacity: enableScan && scanMode === "scanOnly" ? 0.4 : 1,
        }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={printerUsed}
            onChange={(e) => setPrinterUsed(e.target.checked)}
          />
        }
        label="Printer Used"
        style={{
          width: "calc(100% - 64px)",
          alignSelf: "flex-start",
          marginLeft: "32px",
        }}
      />
      {printerUsed && (
        <>
          <TextField
            label="Printer Name"
            variant="outlined"
            value={printerName}
            onChange={(e) => setPrinterName(e.target.value)}
            style={{
              width: "calc(100% - 64px)",
            }}
          />
          <TextField
            label="Receipt Width"
            variant="outlined"
            type="number"
            value={receiptWidth}
            onChange={(e) => setReceiptWidth(e.target.value)}
            inputProps={{ min: 20 }}
            style={{
              width: "calc(100% - 64px)",
            }}
          />
        </>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={enableScan}
            onChange={(e) => setEnableScan(e.target.checked)}
          />
        }
        label="Enable Scan"
        style={{
          width: "calc(100% - 64px)",
          alignSelf: "flex-start",
          marginLeft: "32px",
        }}
      />
      {enableScan && (
        <div
          style={{
            width: "calc(100% - 64px)",
            display: "flex",
            flexDirection: "row",
            gap: "24px",
            marginLeft: "32px",
          }}
        >
          <FormControlLabel
            control={
              <input
                type="radio"
                name="scanMode"
                value="scanOnly"
                checked={scanMode === "scanOnly"}
                onChange={() => setScanMode("scanOnly")}
                style={{ marginRight: "8px" }}
              />
            }
            label="Scan Only"
          />
          <FormControlLabel
            control={
              <input
                type="radio"
                name="scanMode"
                value="scanAndBrowse"
                checked={scanMode === "scanAndBrowse"}
                onChange={() => setScanMode("scanAndBrowse")}
                style={{ marginRight: "8px" }}
              />
            }
            label="Scan and Browse"
          />
        </div>
      )}
      <Button
        variant="contained"
        color="primary"
        onClick={handleSetConfig}
        style={{
          width: "calc(100% - 64px)",
        }}
        disabled={!enableScan && !allowAllStores && selectedStores.length === 0}
      >
        Set Configuration
      </Button>
    </div>
  );
}

const inputStyle = {
  padding: "16px",
  border: "1px solid #ccc",
  borderRadius: "6px",
  fontSize: "14px",
};

const optionStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "10px 14px",
  cursor: "pointer",
  borderBottom: "1px solid #f1f1f1",
  fontSize: "14px",
};

const statusRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  marginTop: "8px",
  color: "#b42318",
  fontSize: "14px",
};
